import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { fetchReturns } from "@/lib/api";
import { RENTAL_STATUS_LABELS, RENTAL_STATUS_COLORS, formatDateTime, isOverdue } from "@/lib/constants";
import { WarningCircle } from "@phosphor-icons/react";

export default function ReturnsPage() {
  const navigate = useNavigate();
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReturns().then(setReturns).catch(console.error).finally(() => setLoading(false));
  }, []);

  return (
    <div className="h-full overflow-y-auto" data-testid="returns-page">
      <div className="max-w-6xl mx-auto p-6 lg:p-8 space-y-6">
        <div>
          <h1 className="text-3xl font-black tracking-tight font-heading text-slate-900">Returns</h1>
          <p className="text-sm text-slate-500 mt-1 font-medium">Vehicles currently on hire and pending return</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          {loading ? <div className="p-8 text-center text-sm text-slate-400">Loading...</div> :
          returns.length === 0 ? (
            <div className="p-8 text-center space-y-3">
              <p className="text-sm font-semibold text-slate-700">No vehicles are currently out on hire.</p>
              <p className="text-sm text-slate-400">Returns will appear here once a rental has been picked up and is waiting to come back.</p>
              <button
                type="button"
                onClick={() => navigate("/rentals")}
                className="inline-flex items-center justify-center h-10 px-4 rounded-md border border-slate-200 text-sm font-bold text-slate-700 hover:bg-slate-50"
              >
                View Rentals
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="returns-table">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 whitespace-nowrap">
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Rental #</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Customer</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Vehicle</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Return Due</th>
                    <th className="text-left px-4 py-3 text-[10px] font-bold uppercase tracking-wider text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {returns.map(r => {
                    const overdue = isOverdue(r.return_datetime) && r.status === "ON_HIRE";
                    const sc = RENTAL_STATUS_COLORS[r.status] || {};
                    return (
                      <tr key={r.id} data-testid={`return-row-${r.rental_number}`}
                        onClick={() => navigate(`/rentals/${r.id}`)}
                        className={`hover:bg-slate-50 cursor-pointer transition-colors whitespace-nowrap ${overdue ? "bg-red-50/50" : ""}`}>
                        <td className="px-4 py-3 font-mono font-bold text-foreground">{r.rental_number}</td>
                        <td className="px-4 py-3 font-semibold text-slate-800">{r.customer_name}</td>
                        <td className="px-4 py-3 text-slate-600">{r.vehicle_display}</td>
                        <td className="px-4 py-3">
                          <span className={`text-sm font-semibold flex items-center gap-1.5 ${overdue ? "text-red-600" : "text-slate-700"}`}>
                            {overdue && <WarningCircle weight="fill" className="w-4 h-4" />}
                            {formatDateTime(r.return_datetime)}
                          </span>
                          {overdue && <span className="text-[10px] font-bold text-red-500 uppercase">Overdue</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-[10px] font-bold uppercase px-2 py-1 rounded-md border" style={{ background: sc.bg, color: sc.text, borderColor: sc.border }}>
                            {RENTAL_STATUS_LABELS[r.status]}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
